import { type JSX } from 'react';
import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Listbox } from '../../../components/listbox';
import { withControl } from '../../../hoc/withControl';
import { ResidentTypesFilterGate, residentTypesFilterQuery } from '../model';
import type { ResidentTypeFilter } from '../types';

type Props = {
  disableClearable?: boolean;
};

export const ResidentTypeFilterControl = withControl<Props>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    isError,
    disableClearable,
  }) => {
    useGate(ResidentTypesFilterGate);

    const { data, pending } = useUnit(residentTypesFilterQuery);

    const findedValue =
      value !== ''
        ? (data?.find(
            (item: ResidentTypeFilter) =>
              item.id === (typeof value === 'object' ? value?.id : value)
          ) ?? null)
        : null;

    const handleChange = (_: unknown, newValue: any) => setValue(newValue || null);

    return (
      <FormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          loading={pending}
          value={findedValue}
          options={data || []}
          onChange={handleChange}
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              slotProps={{
                input: {
                  ...params.InputProps,
                  readOnly,
                  error: isError,
                  endAdornment: readOnly ? null : (
                    <>
                      {pending && <CircularProgress size={20} color="inherit" />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
          disableClearable={disableClearable}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
